import { resources } from "@constants/hardcoded-strings";
import { WorkflowVisualizationContextMenu } from "@containers/common/workflow-visualization-container/workflow-visualization-policy-configs-container/workflow-visualization-context-menu/workflow-visualization-context-menu";
import { ConfirmModal } from "@containers/workflow-details/workflow-details-component/workflow-details-general-tab/translation-tab-container/confirm-modal/confirm-modal";
import { useModalContext } from "@context/use-modal-context";
import { ModalTypes } from "@enums/modal-types";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { useMemo } from "react";

type WorkflowVisualizationServiceContextMenuProps = {
    id: string;
    top: number;
    left: number;
    right: number;
    bottom: number;
    menuRef: React.MutableRefObject<any>;
    rpServices: Array<FormattedRpServiceConfiguration>;
    hasUnsubmittedChangeRequest: boolean;
    primaryRpServiceId: string;
    onPaneClick: () => void;
    onClickOpenServiceDetails: (service: FormattedRpServiceConfiguration) => void;
    onClickArchiveService: (id: string) => void;
    onClickSetAsPrimaryService: (id: string) => void;
};

export const WorkflowVisualizationServiceContextMenu = (props: WorkflowVisualizationServiceContextMenuProps) => {
    const {
        id: serviceId,
        top,
        left,
        right,
        bottom,
        menuRef,
        rpServices,
        hasUnsubmittedChangeRequest,
        primaryRpServiceId,
        onPaneClick,
        onClickOpenServiceDetails,
        onClickArchiveService,
        onClickSetAsPrimaryService
    } = props;

    const resource = resources.common.containers.workflowVisualization.sections.menu;

    const modalContext = useModalContext();

    const onClickOpenDetails = (id: string) => {
        const rpService = rpServices.find(rps => rps.id === id);
        if (rpService) {
            onClickOpenServiceDetails(rpService);
        }
    };

    const handleOpenModalArchiveService = () => {
        const resource = resources.modals.archiveWorkflowService;

        const renderModal = (onModalClose: () => void) => {
            return (
                <ConfirmModal
                    id={"confirm-archive-verification-workflow-service"}
                    title={resource.title}
                    confirmMessage={resource.confirmMessage}
                    cancelButtonText={resources.common.buttons.cancel.label}
                    submitButtonText={resource.submitButtonText}
                    onSubmit={onClickArchiveService}
                    onSubmitAsync={null}
                    onModalClose={onModalClose}
                    customProps={serviceId}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.CONFIRM,
            renderFunction: renderModal
        });
    }

    const menuActions = useMemo(() => {
        const actions = [];
        if (serviceId) {
            actions.push({
                actionId: "open-details",
                label: resource.openServiceDetails.label,
                action: onClickOpenDetails
            });

            const service = rpServices.find(s => s.id === serviceId);

            if (
                onClickSetAsPrimaryService &&
                service?.id !== primaryRpServiceId &&
                hasUnsubmittedChangeRequest
            ) {
                actions.push({
                    actionId: "set-as-primary-service",
                    label: resource.setAsPrimaryService.label,
                    action: onClickSetAsPrimaryService
                });
            }
            if (
                onClickArchiveService &&
                !!service?.permissions?.canArchive &&
                hasUnsubmittedChangeRequest
            ) {
                actions.push({
                    actionId: "archive",
                    label: resource.archiveService.label,
                    action: handleOpenModalArchiveService,
                    disabled: service?.id === primaryRpServiceId,
                    tooltip: resource.archiveService.primaryServiceTooltip
                });
            }
        }

        return actions;
    }, [serviceId, left, right, top, bottom]);

    return (
        <WorkflowVisualizationContextMenu
            onClick={onPaneClick}
            id={serviceId}
            top={top}
            left={left}
            right={right}
            bottom={bottom}
            menuRef={menuRef}
            actions={menuActions}
        />
    );
};